/**
 * Person setup (board onboarding-r2): the steps every founder and teammate runs, rerunnable as a tutorial. The API
 * keeps each person's progress (GET /api/me/setup): observed steps tick from real signals (devices, an agent
 * sign-in, Shopify), marked steps when the person says so. Company setup is a run; a founder who starts their own
 * computer sees it as one step first, "Start your computer". Pure.
 */
import { lineOk, type MachineLine } from './machine'
import type { CheckKey, ClaudeStatus, CodexStatus, FounderWorld, PersonSetup, PersonStepKey, Progress, SetupStepView, TemplateName } from './schemas'
import { PERSON_STEP_KEYS } from './schemas'

export const SETUP_STEPS = [
  { key: 'region', name: 'Start your computer', kind: 'Choose' },
  { key: 'signin', name: 'Sign in', kind: 'Done' },
  { key: 'phone', name: 'Pair a device', kind: 'Guided · Connect' },
  { key: 'agent', name: 'Pick your agent and sign in', kind: 'Connect' },
  { key: 'data', name: 'Connect your data', kind: 'Guided' },
  { key: 'build', name: 'Build a first screen', kind: 'Guided' },
  { key: 'ship', name: 'Ship', kind: 'Connect' },
] as const
export type SetupStepKey = (typeof SETUP_STEPS)[number]['key']
export const ALL_STEP_KEYS: SetupStepKey[] = SETUP_STEPS.map((s) => s.key)
export const STEP_NAME = Object.fromEntries(SETUP_STEPS.map((s) => [s.key, s.name])) as Record<SetupStepKey, string>

export const isStepKey = (k: string | undefined): k is SetupStepKey => ALL_STEP_KEYS.includes(k as SetupStepKey)

/** What the founder or teammate sees live, beyond the server's last answer: the agents' own sign-in status. */
export interface LiveSignIns {
  claude: ClaudeStatus | null
  codex: CodexStatus | null
}

export interface SetupCtx {
  world: Pick<FounderWorld, 'box' | 'run' | 'checks'>
  setup: PersonSetup
  live: LiveSignIns
}

const PROVISIONING: TemplateName[] = ['full', 'adopt', 'boxOnly']

/**
 * Whether the founder still has to pick a region and start their computer: no box and no provisioning run. A run
 * that failed counts as started; a person on our side picks it up, so the founder never sees the picker twice.
 */
export function needsProvision(world: Pick<FounderWorld, 'box' | 'run'>): boolean {
  return !world.box && !(world.run && PROVISIONING.includes(world.run.template))
}

/** The setup an older API can't send: everything open but signing in, completion from the old progress marks. */
export function fallbackSetup(progress: Progress | undefined): PersonSetup {
  return {
    path: 'self',
    agent: 'claude',
    agentPicked: false,
    completedAt: progress?.setupCompletedAt ?? null,
    settled: false,
    devices: null,
    steps: PERSON_STEP_KEYS.map((key) => (key === 'signin' ? { key, status: 'done', how: 'observed', at: null } : { key, status: 'open', how: null, at: null })),
  }
}

/** The steps this person sees: the company step only for a founder who starts their own computer. */
export function visibleSteps(ctx: Pick<SetupCtx, 'setup'>): SetupStepKey[] {
  return ctx.setup.path === 'self' ? ALL_STEP_KEYS : ALL_STEP_KEYS.filter((k) => k !== 'region')
}

export type StepState = Pick<SetupStepView, 'status' | 'how' | 'at' | 'by' | 'detail'>

/** One step's state: the server's, with the chosen agent's live sign-in on top so the tick lands at once. */
export function stepState(key: SetupStepKey, ctx: SetupCtx): StepState {
  if (key === 'region') return needsProvision(ctx.world) ? { status: 'open', how: null, at: null } : { status: 'done', how: 'observed', at: null }
  const s = ctx.setup.steps.find((x) => x.key === key) ?? { status: 'open' as const, how: null, at: null }
  if (key === 'agent' && s.status !== 'done') {
    const live = ctx.setup.agent === 'codex' ? ctx.live.codex : ctx.live.claude
    if (live?.loggedIn) return { status: 'done', how: 'observed', at: null }
  }
  return s
}

export const stepDone = (key: SetupStepKey, ctx: SetupCtx) => stepState(key, ctx).status === 'done'
/** Done or skipped: the walk moves past it. */
export const stepSettled = (key: SetupStepKey, ctx: SetupCtx) => stepState(key, ctx).status !== 'open'

/** The first step not settled, or the last one. */
export function firstOpenStep(ctx: SetupCtx): SetupStepKey {
  const keys = visibleSteps(ctx)
  return keys.find((k) => !stepSettled(k, ctx)) ?? keys.at(-1)!
}

/** Nothing means much until the computer exists, for a founder who starts it themselves. */
export function canView(key: SetupStepKey, ctx: SetupCtx): boolean {
  if (!visibleSteps(ctx).includes(key)) return false
  return key === 'region' || ctx.setup.path !== 'self' || !needsProvision(ctx.world)
}

export function nextStep(key: SetupStepKey, ctx: Pick<SetupCtx, 'setup'>): SetupStepKey | null {
  const keys = visibleSteps(ctx)
  return keys[keys.indexOf(key) + 1] ?? null
}

export function prevStep(key: SetupStepKey, ctx: Pick<SetupCtx, 'setup'>): SetupStepKey | null {
  const keys = visibleSteps(ctx)
  return keys[keys.indexOf(key) - 1] ?? null
}

/** The check a step waits on before it can be used. */
export const STEP_NEEDS: Partial<Record<SetupStepKey, CheckKey>> = {
  phone: 'phone', // a pairing link needs Paseo running on the computer
  agent: 'user', // signing in runs as the person's own user
}

export interface Gate {
  locked: boolean
  waitingFor: CheckKey | null
}

export function stepGate(key: SetupStepKey, lines: MachineLine[]): Gate {
  const need = STEP_NEEDS[key]
  if (!need || lineOk(lines, need)) return { locked: false, waitingFor: null }
  return { locked: true, waitingFor: need }
}

/** Setup is complete once the person finished it; the server keeps it, so every device agrees. */
export const setupComplete = (ctx: Pick<SetupCtx, 'setup'>) => ctx.setup.completedAt !== null

/** A rerun: setup finished before, so it walks as a tutorial. Done steps say when; nothing is undone. */
export const tutorialMode = setupComplete

/** Steps a person can mark; the rest are observed. */
export const markable = (key: SetupStepKey): key is Exclude<PersonStepKey, 'signin'> => key !== 'region' && key !== 'signin'

/** "done on 13 Oct". */
export function doneOn(at: string | null): string | null {
  if (!at) return null
  return new Date(at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}
